<template>
  <component :is="tag"
             class="sdui-icon-wrapper"
             :class="computedClasses"
             v-bind="attrs"
             data-test="sdui-icon"
             aria-hidden="true"
  >
    <slot></slot>
  </component>
</template>

<script setup lang="ts">
import { computed, useAttrs, useSlots } from 'vue'
import { useHasSlotContent } from '@/composables/utils/useHasSlotContent.ts'

interface PropTypes {
  variant?: string;
  bgVariant?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  bgType?: 'none' | 'light' | 'dark';
  tag?: string;
}

const props = withDefaults(defineProps<PropTypes>(), {
  variant: undefined,
  bgVariant: undefined,
  size: 'sm',
  bgType: 'light',
  tag: 'span',
})

const attrs = useAttrs()
const slots = useSlots()
const { hasSlotContent } = useHasSlotContent()

const computedClasses = computed(() => {
  const variantClass = props.variant ? { [`text-${props.variant}`]: props.variant } : {}
  return [
    props.size,
    {
      icon: true,
      ...variantClass,
      [`${props.bgVariant || props.variant}${props.bgType === 'none' ? '' : `-${props.bgType}`}-bg`]:
        props.bgVariant || (!props.bgVariant && props.variant),
      circle: props.bgVariant || (props.bgVariant === undefined && props.variant),
      text: hasSlotContent(slots.default),
    },
  ]
})
</script>

<style lang="scss">
// TODO: check why this is not scoped
.circle {
  vertical-align: middle !important;
  height: 36px;
  width: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: space-around;
  /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
  font-size: 20px;

  &.text {
    /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
    font-size: 12px;

    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: center;
    }
  }

  &.xxl {
    height: 114px;
    width: 114px;
    /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
    font-size: 72px !important;
  }

  &.xl {
    height: 88px;
    width: 88px;
    /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
    font-size: 56px !important;
  }

  &.lg {
    height: 56px;
    width: 56px;
    /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
    font-size: 28px !important;
  }

  &.xs {
    height: 28px;
    width: 28px;
  }
  &.xss {
    height: 20px;
    width: 20px;

    /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
    font-size: 16px;
  }
}

.sdui-icon {
  &.sdui-icon-outlined {
    border-color: $color-gray-100;
    color: $color-gray-300;
    border-style: solid;
    border-width: 1px;
    background-color: transparent;
  }

  &.sdui-icon-expired {
    color: $color-gray-300;
    background-color: $color-secondary;
  }
}

// TODO: check why this is necessary
.icon.small {
  /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
  font-size: 14px;
}

.sdui-icon-wrapper {
  display: inline-flex;
}
</style>
